// La curva de "Tu escalera hoy", calcada del mock (`mcCurveChart`): mismo viewBox 620×240, mismos
// paddings, reja punteada y "AQUÍ VAS". Lo ya vendido va en gris, recto de $0 a lo que lleva: el motor
// no manda el acumulado venta por venta. Lo que puede ganar va en primary, un punto por venta.
import { useId, type ReactNode } from 'react';
import { pesos } from '@/domains/retail/admin/comisiones';
import { formatMoneyCents, formatShortMXN } from '@/shared/lib/format/money';
import { plural } from '@/shared/lib/format/plural';
import type { Ladder, LadderPoint, LadderUnlock, LadderUnlockKind } from '../lib/ladder';
import {
    BASELINE,
    CHART_HEIGHT as HEIGHT,
    CHART_WIDTH as WIDTH,
    dotBox,
    layoutLabels,
    PAD_LEFT,
    PAD_RIGHT,
    PAD_TOP,
    type LabelLine,
    type LabelSpec,
    type PlacedLabel,
} from '../lib/ladderLabels';

const INNER_WIDTH = WIDTH - PAD_LEFT - PAD_RIGHT;
const INNER_HEIGHT = BASELINE - PAD_TOP;
/** La venta 0 se despega del eje Y para que el punto de "AQUÍ VAS" no tape la marca de $0. */
const PLOT_INSET = 12;
const PLOT_LEFT = PAD_LEFT + PLOT_INSET;
const PLOT_WIDTH = INNER_WIDTH - PLOT_INSET;
/** El eje Y llega por lo menos a $1,000, como el mock. */
const MIN_TOP_CENTS = 100_000;
/** Los pasos del eje Y son redondos: $250, $500, $1,000, $2,000, $2,500, $5,000… */
const NICE_STEPS = [1, 2, 2.5, 5];
const MIN_STEP_CENTS = 25_000;
/** Debajo de esto el eje va en pesos; arriba, en miles. */
const SMALL_AXIS_CENTS = 200_000;

const UNLOCK_COLOR: Record<LadderUnlockKind, string> = {
    escalon: 'hsl(var(--success))',
    meta: 'hsl(var(--warning))',
    multiplicador: 'hsl(var(--info))',
};

/** La sombra de color bajo cada punto de desbloqueo. */
const UNLOCK_SHADOW: Record<LadderUnlockKind, string> = {
    escalon: 'drop-shadow(0 2px 4px hsl(var(--success) / 0.35))',
    meta: 'drop-shadow(0 2px 4px hsl(var(--warning) / 0.4))',
    multiplicador: 'drop-shadow(0 2px 4px hsl(var(--info) / 0.35))',
};

const UNLOCK_ICON: Record<LadderUnlockKind, string | null> = {
    escalon: '✨',
    meta: '⚡',
    multiplicador: null,
};

/** Las etiquetas chicas del mock van en mayúsculas y espaciadas. */
const CAPS = { textTransform: 'uppercase', letterSpacing: '0.05em' } as const;

const unlockName = (unlock: LadderUnlock) =>
    unlock.kind === 'multiplicador' ? `${unlock.text} ${unlock.name}` : unlock.name;

/** Con varios desbloqueos en la misma venta se pinta el primero y "+N"; todos van en el texto accesible. */
const moreText = (unlocks: readonly LadderUnlock[]) =>
    unlocks.length > 1 ? ` +${String(unlocks.length - 1)}` : '';

/** El nombre de lo que se desbloquea; el ícono es decorativo. */
function UnlockName({ unlocks }: { unlocks: readonly [LadderUnlock, ...LadderUnlock[]] }) {
    const [first] = unlocks;
    const icon = UNLOCK_ICON[first.kind];
    return (
        <>
            {icon && <tspan aria-hidden="true">{`${icon} `}</tspan>}
            {`${unlockName(first)}${moreText(unlocks)}`}
        </>
    );
}

/** Caracteres de la etiqueta, con el ícono. */
const unlockLabelChars = (unlocks: readonly [LadderUnlock, ...LadderUnlock[]]) =>
    unlockName(unlocks[0]).length +
    (UNLOCK_ICON[unlocks[0].kind] ? 2 : 0) +
    moreText(unlocks).length;

const pointTitle = (point: LadderPoint) =>
    [
        plural(point.sales, 'venta', 'ventas'),
        plural(point.units, 'pieza', 'piezas'),
        formatMoneyCents(point.cents),
        ...point.unlocks.map((u) => `${u.name} ${u.text}`),
    ].join(' · ');

/** Los desbloqueos dichos en texto: las etiquetas que no caben en la curva no se pintan. */
const unlocksText = (next: readonly LadderPoint[]) =>
    next
        .flatMap((point) =>
            point.unlocks.map((u) => `En la venta ${String(point.sales)}: ${u.text}, ${u.name}.`),
        )
        .join(' ');

/** El paso redondo más chico que cubre `rawCents`. */
function niceStep(rawCents: number): number {
    const wanted = Math.max(rawCents, MIN_STEP_CENTS);
    if (!Number.isFinite(wanted)) return MIN_STEP_CENTS;
    for (let base = 10_000; ; base *= 10) {
        const step = NICE_STEPS.map((m) => m * base).find((candidate) => candidate >= wanted);
        if (step !== undefined) return step;
    }
}

function axisLabel(cents: number, small: boolean): string {
    if (!small) return formatShortMXN(cents / 100);
    return cents < 0 ? `-${pesos(-cents)}` : pesos(cents);
}

interface ChartLine extends LabelLine {
    content: ReactNode;
    weight: number;
}

interface ChartLabel extends LabelSpec<ChartLine> {
    color: string;
}

function LabelText({ label }: { label: PlacedLabel<ChartLabel> }) {
    return label.spec.lines.map((line, i) => (
        <text
            key={i}
            x={label.spec.x}
            y={label.ys[i]}
            textAnchor={label.anchor}
            fontSize={line.size}
            fontWeight={line.weight}
            fill={label.spec.color}
            style={line.caps ? CAPS : undefined}
        >
            {line.content}
        </text>
    ));
}

/** Un monto que no es número no se dibuja: "hoy" va en cero y las ventas por venir se omiten. */
const finiteNow = (point: LadderPoint) =>
    Number.isFinite(point.cents) ? point : { ...point, cents: 0 };

export function LadderChart({ ladder }: { ladder: Ladder }) {
    const descId = useId();
    const now = finiteNow(ladder.now);
    const next = ladder.next.filter((point) => Number.isFinite(point.cents));
    const all = [now, ...next];
    const last = next.at(-1) ?? now;
    const maxSales = Math.max(1, last.sales);
    const values = all.map((p) => p.cents);
    const maxCents = Math.max(...values);
    const minCents = Math.min(...values);
    const yStep = niceStep((Math.max(maxCents, 0) - Math.min(minCents, 0)) / 4);
    const top = Math.max(Math.ceil(MIN_TOP_CENTS / yStep), Math.ceil(maxCents / yStep)) * yStep;
    const bottom = Math.min(0, Math.floor(minCents / yStep) * yStep);
    const smallAxis = maxCents < SMALL_AXIS_CENTS;
    const x = (sales: number) => PLOT_LEFT + (sales / maxSales) * PLOT_WIDTH;
    const y = (cents: number) =>
        PAD_TOP + INNER_HEIGHT - ((cents - bottom) / (top - bottom)) * INNER_HEIGHT;
    // Con neto negativo el área se rellena hasta $0, no hasta el piso del eje.
    const zeroY = y(0);

    const line = (points: LadderPoint[]) =>
        points
            .map((p, i) => `${i === 0 ? 'M' : 'L'}${String(x(p.sales))},${String(y(p.cents))}`)
            .join(' ');
    const area = (points: LadderPoint[]) => {
        const first = points[0];
        const end = points.at(-1) ?? first;
        return `${line(points)} L${String(x(end.sales))},${String(zeroY)} L${String(x(first.sales))},${String(zeroY)} Z`;
    };
    const origin: LadderPoint = { sales: 0, units: 0, cents: 0, unlocks: [] };
    const past = now.sales > 0 ? [origin, now] : null;
    const future = [now, ...next];
    const ticks = Array.from(
        { length: Math.round((top - bottom) / yStep) + 1 },
        (_, i) => bottom + i * yStep,
    );
    // Una etiqueta cada 2 ventas, como el mock; con muchas, menos para que no se encimen.
    const tickEvery = Math.max(2, Math.ceil(maxSales / 10));
    const xTicks = Array.from({ length: maxSales + 1 }, (_, i) => i).filter(
        (i) => i % tickEvery === 0 || i === maxSales,
    );
    const nowX = x(now.sales);
    const nowY = y(now.cents);

    const nowLabel: ChartLabel = {
        key: 'now',
        x: nowX,
        y: nowY,
        gap: 8,
        color: 'hsl(var(--primary))',
        lines: [
            { content: 'AQUÍ VAS', chars: 8, size: 10, weight: 700, caps: true },
            {
                content: formatMoneyCents(now.cents),
                chars: formatMoneyCents(now.cents).length,
                size: 11,
                weight: 700,
                caps: false,
            },
        ],
    };
    const unlockLabels: ChartLabel[] = next.flatMap((point) => {
        const first = point.unlocks.at(0);
        if (!first) return [];
        const unlocks = [first, ...point.unlocks.slice(1)] as const;
        const amount = formatMoneyCents(point.cents);
        return [
            {
                key: `unlock-${String(point.sales)}`,
                x: x(point.sales),
                y: y(point.cents),
                gap: 7,
                color: UNLOCK_COLOR[first.kind],
                lines: [
                    {
                        content: <UnlockName unlocks={unlocks} />,
                        chars: unlockLabelChars(unlocks),
                        size: 8.5,
                        weight: 600,
                        caps: true,
                    },
                    { content: amount, chars: amount.length, size: 10, weight: 700, caps: false },
                ],
            },
        ];
    });
    const lastAmount = formatMoneyCents(last.cents);
    const endLabel: ChartLabel[] =
        last.unlocks.length === 0 && next.length > 0
            ? [
                  {
                      key: 'end',
                      x: x(last.sales),
                      y: y(last.cents),
                      gap: 8,
                      color: 'hsl(var(--primary))',
                      lines: [
                          {
                              content: lastAmount,
                              chars: lastAmount.length,
                              size: 10,
                              weight: 700,
                              caps: false,
                          },
                      ],
                  },
              ]
            : [];
    const labels = layoutLabels(nowLabel, [...unlockLabels, ...endLabel], {
        dots: [
            { key: nowLabel.key, box: dotBox(nowX, nowY, 11) },
            ...unlockLabels.map((spec) => ({ key: spec.key, box: dotBox(spec.x, spec.y, 10) })),
        ],
        curve: (past ? [origin, ...future] : future).map((p) => ({ x: x(p.sales), y: y(p.cents) })),
    });
    const description = unlocksText(next);

    return (
        <svg
            viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
            preserveAspectRatio="xMidYMid meet"
            className="block min-h-[240px] w-full flex-1 tabular-nums"
            role="img"
            aria-label={`Lo que llevarías con ${plural(next.length, 'venta más', 'ventas más')}: de ${formatMoneyCents(now.cents)} a ${formatMoneyCents(last.cents)}`}
            aria-describedby={description ? descId : undefined}
        >
            {description && <desc id={descId}>{description}</desc>}
            {ticks.map((value) => (
                <g key={value}>
                    <line
                        x1={PAD_LEFT}
                        x2={WIDTH - PAD_RIGHT}
                        y1={y(value)}
                        y2={y(value)}
                        stroke="hsl(var(--border))"
                        strokeDasharray="3 4"
                        opacity={0.6}
                    />
                    <text
                        x={PAD_LEFT - 6}
                        y={y(value) + 3}
                        textAnchor="end"
                        fontSize={10}
                        fill="hsl(var(--muted-foreground))"
                    >
                        {axisLabel(value, smallAxis)}
                    </text>
                </g>
            ))}

            {past && (
                <>
                    <path d={area(past)} fill="hsl(var(--muted-foreground) / 0.08)" />
                    <path
                        d={line(past)}
                        fill="none"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={2}
                    />
                </>
            )}
            <path d={area(future)} fill="hsl(var(--primary) / 0.12)" />
            <path
                d={line(future)}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
            />

            {next.map((point) => {
                const unlock = point.unlocks.at(0);
                return (
                    <circle
                        key={point.sales}
                        cx={x(point.sales)}
                        cy={y(point.cents)}
                        r={unlock ? 7 : 3}
                        fill={unlock ? UNLOCK_COLOR[unlock.kind] : 'hsl(var(--primary))'}
                        stroke="white"
                        strokeWidth={unlock ? 3 : 2}
                        style={unlock ? { filter: UNLOCK_SHADOW[unlock.kind] } : undefined}
                    >
                        <title>{pointTitle(point)}</title>
                    </circle>
                );
            })}

            <line
                x1={nowX}
                x2={nowX}
                y1={BASELINE}
                y2={Math.min(nowY + 12, BASELINE)}
                stroke="hsl(var(--primary))"
                strokeWidth={1.5}
                strokeDasharray="4 3"
            />
            <circle
                cx={nowX}
                cy={nowY}
                r={8}
                fill="white"
                stroke="hsl(var(--primary))"
                strokeWidth={3}
            >
                <title>{pointTitle(now)}</title>
            </circle>

            {labels.map((label) => (
                <LabelText key={label.spec.key} label={label} />
            ))}

            {xTicks.map((sales) => (
                <text
                    key={sales}
                    x={x(sales)}
                    y={HEIGHT - 16}
                    textAnchor="middle"
                    fontSize={10}
                    fill="hsl(var(--muted-foreground))"
                >
                    {sales}
                </text>
            ))}
            <text
                x={PAD_LEFT + INNER_WIDTH / 2}
                y={HEIGHT - 3}
                textAnchor="middle"
                fontSize={9}
                fontWeight={700}
                fill="hsl(var(--muted-foreground))"
                style={CAPS}
            >
                VENTAS EN EL CORTE
            </text>
        </svg>
    );
}

const LEGEND: ReadonlyArray<{ label: string; icon?: string; dot: string }> = [
    { label: 'Lo que ya llevas', dot: 'bg-muted-foreground' },
    { label: 'Lo que puedes ganar', dot: 'bg-primary' },
    { label: 'Escalón', icon: '✨', dot: 'bg-success' },
    { label: 'Meta', icon: '⚡', dot: 'bg-warning' },
    { label: 'Multiplicador', dot: 'bg-info' },
    { label: 'Aquí vas', dot: 'border-2 border-primary bg-card' },
];

/** La leyenda del pie, con los colores de la curva. */
export function LadderLegend() {
    return (
        <ul className="flex flex-wrap gap-2 pt-2.5 text-[10px] text-muted-foreground">
            {LEGEND.map((item) => (
                <li key={item.label} className="inline-flex items-center gap-[5px]">
                    <span
                        className={`inline-block size-[9px] rounded-full ${item.dot}`}
                        aria-hidden
                    />
                    {item.icon && <span aria-hidden>{item.icon}</span>}
                    {item.label}
                </li>
            ))}
        </ul>
    );
}
